import { useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Button, Checkbox, OverlayPopover, Select } from '@utilities';
import {
  DEFAULT_SIGNIFICANCE_ALPHA,
  SIGNIFICANCE_CONFIDENCE_LEVELS,
} from '@utilities/helpers/significance';

import styles from './_index.module.scss';

const CONFIDENCE_OPTIONS = SIGNIFICANCE_CONFIDENCE_LEVELS.map(({ alpha, label }) => ({
  label,
  value: alpha,
}));

const HELP_URL =
  'https://help.numerator.com/en/articles/10149546-in-platform-survey-results-about-crosstabs-and-significance-testing';

const SignificanceControls = ({ alpha, inputId, isDisabled, onChange }) => {
  const popoverTargetRef = useRef(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const isEnabled = alpha != null;
  // Fall back to the default so an unrecognized alpha never renders an
  // empty select or button suffix.
  const selectedOption =
    CONFIDENCE_OPTIONS.find((opt) => opt.value === alpha) ??
    CONFIDENCE_OPTIONS.find((opt) => opt.value === DEFAULT_SIGNIFICANCE_ALPHA);

  return (
    <div className={styles['significance-controls']} ref={popoverTargetRef}>
      <Button
        className={classNames(styles['significance-controls-button'], {
          [styles['is-applied']]: isEnabled,
        })}
        isDisabled={isDisabled}
        onClick={() => setIsPopoverOpen(true)}
        text={
          isEnabled
            ? `Statistical Significance: ${selectedOption.label}`
            : 'Statistical Significance'
        }
        variant="secondary"
      />
      <OverlayPopover
        className={styles['significance-controls-popover']}
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        target={popoverTargetRef.current}
      >
        <div className={styles['significance-controls-content']}>
          <Checkbox
            id={inputId}
            isChecked={isEnabled}
            isDisabled={isDisabled}
            label="Show statistical significance"
            onChange={() => onChange?.(isEnabled ? null : DEFAULT_SIGNIFICANCE_ALPHA)}
            value={isEnabled}
          />
          <p className={styles['significance-controls-description']}>
            Letters mark columns significantly higher than the lettered column(s).{' '}
            <a href={HELP_URL} rel="noreferrer" target="_blank">
              Learn more
            </a>
          </p>
          {isEnabled && (
            <Select
              className={styles['significance-controls-select']}
              isDisabled={isDisabled}
              label="Confidence"
              onChange={(opt) => onChange?.(opt?.value ?? DEFAULT_SIGNIFICANCE_ALPHA)}
              options={CONFIDENCE_OPTIONS}
              value={selectedOption}
            />
          )}
        </div>
      </OverlayPopover>
    </div>
  );
};

SignificanceControls.defaultProps = {
  alpha: undefined,
  inputId: 'show-significance',
  isDisabled: false,
};

SignificanceControls.propTypes = {
  /** One-tailed alpha for the selected confidence; `undefined` = significance off. */
  alpha: PropTypes.number,
  /**
   * Unique id for the enable checkbox. Required when more than one instance is
   * rendered (e.g. one per question) so each checkbox/label pair is distinct —
   * a shared id makes every label toggle the first checkbox in document order.
   */
  inputId: PropTypes.string,
  isDisabled: PropTypes.bool,
  /** Called with the new alpha, or `null` to disable. */
  onChange: PropTypes.func,
};

export default SignificanceControls;
